import { AppointmentPhase, Status } from "components";
import FilterHeader from "components/filters/FilterHeader";
import { FilterProvider, type FilterProviderRef, type FilterValues } from "components/filters/FilterContext";
import ArtistFilter from "components/filters/Filters/ArtistFilter";
import BookkeepingFilter from "components/filters/Filters/BookkeepingFilter";
import KeywordFilter from "components/filters/Filters/KeywordFilter";
import PeriodFilter from "components/filters/Filters/PeriodFilter";
import ProductionFilter from "components/filters/Filters/ProductionFilter";
import StatusFilter from "components/filters/Filters/StatusFilter";
import { mapArtistFilterToInstruction, mapBookkeepingFilterToInstruction, mapKeywordFilterToInstruction, mapPeriodFilterToInstruction, mapProductionFilterToInstruction, mapResponseToArtistFilter, mapResponseToBookkeepingFilter, mapResponseToKeywordFilter, mapResponseToPeriodFilter, mapResponseToProductionFilter, mapResponseToStatusFilter, mapStatusFilterToInstruction } from "components/filters/mappingUtils";
import PickerList from "components/Picker/PickerList";
import { useStatuses } from "components/Status/hooks";
import React from "react";
import ShowDateTime from "show/components/ShowDateTime";
import { PickerListProps } from "../props";

export interface ShowListProps extends PickerListProps<server.dto.ShowForListInternal> {
  defaultFilter: server.dto.ListShowInternalFilters
  initialInstruction: server.dto.PaginatedShowInternalInstruction
  instructionOverrides?: Partial<server.dto.PaginatedShowInternalInstruction>
  perPage?: number
  queryKey?: string
}

const ShowList = (props: ShowListProps) => {
  const perPage = props.perPage ?? 20;
  const statusesQuery = useStatuses("show");
  const filterRef = React.useRef<FilterProviderRef>(null);
  const initialLoadingRef = React.useRef(true);
  const [initialLoading, setInitialLoading] = React.useState(true);

  const applyInstructionSettings = (
    nextInstruction: server.dto.PaginatedShowInternalInstruction,
    page: number,
    pageSize: number
  ): server.dto.PaginatedShowInternalInstruction => {
    const instruction = {
      ...nextInstruction,
      ...props.instructionOverrides,
      page,
      perPage: pageSize
    } satisfies server.dto.PaginatedShowInternalInstruction;

    if (props.excludeKeys != null) {
      instruction.excludeShowIds = props.excludeKeys;
    }

    return instruction;
  };

  const [instruction, setInstruction] = React.useState<server.dto.PaginatedShowInternalInstruction>(
    applyInstructionSettings(props.initialInstruction, 1, perPage)
  );

  React.useEffect(() => {
    setInstruction(currentInstruction => applyInstructionSettings(currentInstruction, 1, perPage));
  }, [props.excludeKeys, perPage]);

  const mapResponseToFilterValues = (
    response: server.dto.ListShowInternalFilters,
    values: FilterValues
  ): Partial<FilterValues> => ({
    keyword: mapResponseToKeywordFilter(response.keyword),
    artist: mapResponseToArtistFilter(response.artists),
    bookkeepings: mapResponseToBookkeepingFilter(response.bookkeepings),
    statuses: mapResponseToStatusFilter(response.statuses, values.statuses),
    period: mapResponseToPeriodFilter(response.period),
    production: mapResponseToProductionFilter(response.productions)
  });

  const mapFilterValuesToInstruction = (
    filter: FilterValues,
    page: number,
    pageSize: number
  ): server.dto.PaginatedShowInternalInstruction => {
    const nextInstruction = {
      keyword: mapKeywordFilterToInstruction(filter.keyword),
      artistIds: mapArtistFilterToInstruction(filter.artist),
      bookkeepingIds: mapBookkeepingFilterToInstruction(filter.bookkeepings),
      statuses: mapStatusFilterToInstruction(filter.statuses),
      period: mapPeriodFilterToInstruction(filter.period),
      productionIds: mapProductionFilterToInstruction(filter.production),
      page,
      perPage: pageSize
    } satisfies server.dto.PaginatedShowInternalInstruction;

    return applyInstructionSettings(nextInstruction, page, pageSize);
  };

  const onFilterChange = (filter: FilterValues) => {
    setInstruction(mapFilterValuesToInstruction(filter, 1, perPage));
  };

  const api = (currentInstruction: server.dto.PaginatedShowInternalInstruction) => {
    return window.CoreApi.Show.PaginatedInternalEnvironment(currentInstruction).then(response => {
      const filterProvider = filterRef.current;

      if (filterProvider != null) {
        if (initialLoadingRef.current) {
          filterProvider.setDefaultValues(prev => mapResponseToFilterValues(props.defaultFilter, prev));
          initialLoadingRef.current = false;
          setInitialLoading(false);
        }

        filterProvider.updateValues(prev => mapResponseToFilterValues(response.data.request, prev));
      }

      return response;
    });
  };

  return <div className="flex-column grow1 gap-larger" style={{ minHeight: 0 }}>
    <FilterProvider ref={filterRef} onFilterChange={onFilterChange}>
      <PickerList
        instruction={instruction}
        onInstructionChange={setInstruction}
        queryKey={props.queryKey ?? "showpickerlist"}
        api={api}
        onClick={props.onItemSelected}
        getError={() => undefined}
        excludeKeys={props.excludeKeys}
        getId={show => show.showId}
        hideKeywordFilter
        renderListItem={show => <div className="flex">
          <div className="margin-right-small">
            <AppointmentPhase value={show.status.appointmentPhase} displayLabel={false} />
          </div>
          <div className="grow1">
            <div>{show.title}</div>
            <Status status={show.status} small />
            <div>
              <ShowDateTime dateTime={show.dateTime} />
            </div>
          </div>
        </div>}
        otherFilters={<FilterHeader.Filters compact isLoading={initialLoading}>
          <div className="selectiontool-header-filters">
            <KeywordFilter placeholder={RESX.GeneralLabels.Search} />
            <ArtistFilter />
            <ProductionFilter />
            <StatusFilter initialStatuses={statusesQuery.data ?? []} />
            <PeriodFilter />
            <BookkeepingFilter />
          </div>
        </FilterHeader.Filters>}
      />
    </FilterProvider>
  </div>;
};

export default ShowList;
